// hooks.ts - Fixed version with simple pagination like UserTable

import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast, useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { downloadFile } from "@shared/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  assignedJobCards,
  assignJobCards,
  serviceProcedureApi,
  wxApi,
  updateJobCardPriority,
  getSoCounts,
  generateReport,
  getGeneratedReport
} from "./api";
import {
  AssetSearchParams,
  AssignmentRequest,
  ConsumerSearchParams,
  CreateServiceOrderRequest,
  FieldForceRecommendParams,
  JobCardDetailParams,
  JobCardListParamsWithFilters,
  JobCardRecommendationParams,
  MeterSearchParams,
  serviceOrderFilters,
  ServiceOrderFormData,
  ServiceProcedureListParams,
  UseJobCardDetailParams,
  GetSoCountsResponse,
} from "./types";
import { transformJobCardsToServiceOrders } from "./utils";

// ✅ FIXED: Simple filter parsing like UserTable
const useServiceOrdersWithFilters = (
  orderType: "pending" | "assigned" | "completed" | "history"
) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;

  // ✅ FIXED: Simple filter parsing without isActiveTab conditions
  const getFiltersFromUrl = (): serviceOrderFilters => {
    const pageParam = searchParams.get("page");
    const page = pageParam ? parseInt(pageParam) : 1;

    const urlFilters: serviceOrderFilters = {
      page: page, // ✅ ALWAYS use URL page, no conditions
      limit: itemsPerPage,
      remoteUtilityId: remoteUtilityId,
      [orderType === "pending"
        ? "isPending"
        : orderType === "assigned"
        ? "isAssigned"
        : orderType === "completed"
        ? "isCompleted"
        : "isHistory"]: true,
    };

    // ✅ FIXED: Parse all filters without isActiveTab conditions
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "all") {
      urlFilters.status = statusParam;
    }

    const serviceAssociationParam = searchParams.get("serviceAssociation");
    if (serviceAssociationParam && serviceAssociationParam !== "all") {
      urlFilters.serviceAssociation = serviceAssociationParam;
    }

    const priorityParam = searchParams.get("priority");
    if (priorityParam && priorityParam !== "all") {
      urlFilters.priority = priorityParam;
    }

    const areaParam = searchParams.get("area");
    if (areaParam && areaParam !== "all") {
      urlFilters.area = areaParam;
    }

    const technicianParam = searchParams.get("technician");
    if (technicianParam) {
      urlFilters.technicianId = technicianParam;
    }

    const slaParam = searchParams.get("sla");
    if (slaParam && slaParam !== "all") {
      urlFilters.slaFilter = slaParam;
    }

    const soNameParam = searchParams.get("soName");
    if (soNameParam) {
      urlFilters.soNameFilter = soNameParam;
    }

    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.searchData = queryParam;
    }

    return urlFilters;
  };

  // ✅ FIXED: Simple state management like UserTable
  const [filters, setFilters] =
    useState<serviceOrderFilters>(getFiltersFromUrl);

  // ✅ FIXED: Update filters when URL changes - simple approach
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId, orderType]);

  const queryKey = useMemo(
    () => [
      "wx",
      "job-card",
      orderType,
      remoteUtilityId,
      filters.page,
      filters.searchData || "",
      filters.serviceAssociation || "",
      filters.status || "",
      filters.priority || "",
      filters.area || "",
      filters.technicianId || "",
      filters.slaFilter || "",
      filters.soNameFilter || "",
    ],
    [remoteUtilityId, filters, orderType]
  );

  // ✅ FIXED: Simple API call function
  const apiCall = useCallback(() => {
    const params: JobCardListParamsWithFilters = {
      remoteUtilityId,
      filters,
    };

    switch (orderType) {
      case "pending":
        return wxApi.pending(params);
      case "assigned":
        return wxApi.assigned(params);
      case "completed":
        return wxApi.completed(params);
      case "history":
        return wxApi.history(params);
      default:
        throw new Error(`Unknown order type: ${orderType}`);
    }
  }, [remoteUtilityId, filters, orderType]);

  // ✅ FIXED: Always enabled API call - no conditional logic
  const {
    data: apiResponse,
    isLoading,
    error,
    refetch,
  } = useSmartQuery(queryKey, apiCall);

  // Transform data to UI format
  const serviceOrders = useMemo(() => {
    if (!apiResponse?.results) return [];
    return transformJobCardsToServiceOrders(apiResponse.results);
  }, [apiResponse]);

  // Pagination info
  const pagination = useMemo(() => {
    if (!apiResponse) return null;

    const totalItems = apiResponse.count || serviceOrders.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const currentPage = filters.page;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [apiResponse, filters.page, itemsPerPage, serviceOrders.length]);

  // ✅ FIXED: Simple pagination handler like UserTable
  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", newPage.toString());
      setSearchParams(params);
    },
    [setSearchParams, orderType]
  );

  // ✅ FIXED: Simple search handler
  const handleSearch = useCallback(
    (searchTerm: string, paramName: string = "query") => {
      const params = new URLSearchParams(searchParams);

      if (searchTerm.trim()) {
        params.set(paramName, searchTerm);
      } else {
        params.delete(paramName);
      }

      // Reset to page 1 when searching
      params.set("page", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // ✅ FIXED: Simple filter handlers
  const handleApplyFilters = useCallback(
    (newFilters: Partial<serviceOrderFilters>) => {
      const params = new URLSearchParams(searchParams);

      // Clear existing filter params
      [
        "status",
        "serviceAssociation",
        "priority",
        "area",
        "technician",
        "sla",
        "soName",
        "performance",
        "page",
      ].forEach((key) => {
        params.delete(key);
      });

      // Apply new filters
      if (newFilters.status && newFilters.status !== "all") {
        params.set("status", newFilters.status);
      }

      if (
        newFilters.serviceAssociation &&
        newFilters.serviceAssociation !== "all"
      ) {
        params.set("serviceAssociation", newFilters.serviceAssociation);
      }

      if (newFilters.priority && newFilters.priority !== "all") {
        params.set("priority", newFilters.priority);
      }

      if (newFilters.area && newFilters.area !== "all") {
        params.set("area", newFilters.area);
      }

      if (newFilters.technicianId) {
        params.set("technician", newFilters.technicianId);
      }

      if (newFilters.slaFilter && newFilters.slaFilter !== "all") {
        params.set("sla", newFilters.slaFilter);
      }

      if (newFilters.soNameFilter) {
        params.set("soName", newFilters.soNameFilter);
      }

      // Reset to page 1 when applying filters
      params.set("page", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    [
      "status",
      "serviceAssociation",
      "priority",
      "area",
      "technician",
      "sla",
      "soName",
      "performance",
      "page",
      "query",
    ].forEach((key) => {
      params.delete(key);
    });

    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  console.log(`🔍 Hook ${orderType} returning:`, {
    pagination,
    serviceOrdersCount: serviceOrders.length,
    isLoading,
  });

  return {
    serviceOrders,
    isLoading,
    error,
    pagination,
    currentFilters: filters,
    handleSearch,
    handlePageChange,
    handleApplyFilters,
    handleResetFilters,
    refetch,
  };
};

// Individual hooks for each service order type
export const usePendingServiceOrdersWithFilters = () => {
  return useServiceOrdersWithFilters("pending");
};

export const useAssignedServiceOrdersWithFilters = () => {
  return useServiceOrdersWithFilters("assigned");
};

export const useCompletedServiceOrdersWithFilters = () => {
  return useServiceOrdersWithFilters("completed");
};

export const useHistoryServiceOrdersWithFilters = () => {
  return useServiceOrdersWithFilters("history");
};

export const useJobCardDetail = (params: UseJobCardDetailParams) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryParams: JobCardDetailParams = {
    id: params.id.toString(),
    remoteUtilityId: remoteUtilityId,
  };

  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.details(params.id.toString()),
    () => wxApi.detail(queryParams),
    {
      enabled: !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
      // Keep previous data during refetch for smoother UX
    }
  );
};

// Add these export hooks to your hooks file

// Hook for exporting pending service orders
export const usePendingServiceOrdersExport = (filters: serviceOrderFilters) => {
  const [isExporting, setIsExporting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const params: JobCardListParamsWithFilters = {
        remoteUtilityId,
        filters: {
          ...filters,
          page: undefined, // Remove pagination for export
          limit: undefined,
        },
      };

      const blob = await wxApi.exportPending(params);
      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export pending service orders: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remoteUtilityId, filters, isExporting]);

  return { exportCSV, isExporting };
};

// Hook for exporting assigned service orders
export const useAssignedServiceOrdersExport = (
  filters: serviceOrderFilters
) => {
  const [isExporting, setIsExporting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const params: JobCardListParamsWithFilters = {
        remoteUtilityId,
        filters: {
          ...filters,
          page: undefined, // Remove pagination for export
          limit: undefined,
        },
      };

      const blob = await wxApi.exportAssigned(params);

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export assigned service orders: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remoteUtilityId, filters, isExporting]);

  return { exportCSV, isExporting };
};

// Hook for exporting completed service orders
export const useCompletedServiceOrdersExport = (
  filters: serviceOrderFilters
) => {
  const [isExporting, setIsExporting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const params: JobCardListParamsWithFilters = {
        remoteUtilityId,
        filters: {
          ...filters,
          page: undefined, // Remove pagination for export
          limit: undefined,
        },
      };

      const blob = await wxApi.exportCompleted(params);
      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export completed service orders: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remoteUtilityId, filters, isExporting]);

  return { exportCSV, isExporting };
};

// Hook for exporting history service orders
export const useHistoryServiceOrdersExport = (filters: serviceOrderFilters) => {
  const [isExporting, setIsExporting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const params: JobCardListParamsWithFilters = {
        remoteUtilityId,
        filters: {
          ...filters,
          page: undefined, // Remove pagination for export
          limit: undefined,
        },
      };

      const blob = await wxApi.exportHistory(params);

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export service order history: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remoteUtilityId, filters, isExporting]);

  return { exportCSV, isExporting };
};

export const useServiceProcedureList = (
  params?: Partial<ServiceProcedureListParams>,
  useUrlFilters: boolean = false
) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Build query parameters
  const queryParams: ServiceProcedureListParams = useMemo(() => {
    const baseParams = {
      remoteUtilityId,
      page: 1,
      limit: 50,
      isActive: true,
      ...params,
    };

    // If URL filtering is enabled, add URL parameters
    if (useUrlFilters) {
      const searchTerm = searchParams.get("sopSearch");
      const category = searchParams.get("sopCategory");
      const page = searchParams.get("sopPage");

      if (searchTerm) {
        baseParams.searchTerm = searchTerm;
      }

      if (category && category !== "all") {
        // 🐛 DEBUG: Check the category value

        baseParams.sopAssociationTypeDisplay = category;
      }

      if (page) {
        baseParams.page = parseInt(page);
      }
    }

    return baseParams;
  }, [remoteUtilityId, params, useUrlFilters, searchParams]);

  // ✅ FIXED: Update query key to use sopAssociationTypeDisplay
  const queryKey = useMemo(
    () => [
      ...QueryKeyFactory.module.wx.dispatcher.serviceProcedures(),
      queryParams.searchTerm || "",
      queryParams.sopAssociationTypeDisplay || "", // ✅ Changed from sopAssociationType
      queryParams.page || 1,
    ],
    [queryParams]
  );

  return useSmartQuery(
    queryKey,
    () => {
      return serviceProcedureApi.list(queryParams);
    },
    {
      enabled: !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useConsumerSearch = (
  params?: Partial<ConsumerSearchParams>,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryParams: ConsumerSearchParams = {
    remoteUtilityId,
    limit: 5,
    page: 1,
    ...params,
  };

  // Create query key that includes all filter parameters for proper caching
  const queryKey = [
    ...QueryKeyFactory.module.wx.dispatcher.consumerList(),
    queryParams.searchData || "",
    queryParams.page,
    queryParams.limit,
    queryParams.area,
  ];

  return useSmartQuery(queryKey, () => wxApi.consumer(queryParams), {
    enabled: !!(remoteUtilityId && options.enabled !== false),
    staleTime: 2 * 60 * 1000,
    // Keep previous data during refetch for smoother UX
  });
};

// Updated useMeterSearch hook with URL-based filters
export const useAssetSearch = (
  params?: Partial<AssetSearchParams>,
  options: { enabled?: boolean } = {}
) => {
  const queryParams: AssetSearchParams = {
    limit: 5,
    page: 1,
    // sopId: null,
    ...params,
  };

  // Create query key that includes all filter parameters for proper caching
  const queryKey = [
    ...QueryKeyFactory.module.wx.dispatcher.assetList(),
    queryParams.searchData || "",
    queryParams.page,
    queryParams.limit,
    queryParams.sopId,
  ];

  return useSmartQuery(queryKey, () => wxApi.asset(queryParams), {
    enabled: !!(options.enabled !== false),
    staleTime: 2 * 60 * 1000,
    // Keep previous data during refetch for smoother UX
  });
};

export const useMeterSearch = (
  params?: Partial<MeterSearchParams>,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryParams: MeterSearchParams = {
    remoteUtilityId,
    limit: 5,
    page: 1,
    ...params,
  };

  // Create query key that includes all filter parameters for proper caching
  const queryKey = [
    ...QueryKeyFactory.module.wx.dispatcher.meterList(),
    queryParams.searchData || "",
    queryParams.page,
    queryParams.limit,
    queryParams.area,
  ];

  return useSmartQuery(queryKey, () => wxApi.meter(queryParams), {
    enabled: !!(remoteUtilityId && options.enabled !== false),
    staleTime: 2 * 60 * 1000,
    // Keep previous data during refetch for smoother UX
  });
};

export const useCreateServiceOrder = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (formData: ServiceOrderFormData) => {
      if (!utilityId) {
        throw new Error("Utility ID is missing");
      }

      if (
        !formData.selectedSOP ||
        !formData.selectedEntities.length ||
        !formData.priority
      ) {
        throw new Error("Missing required fields: SOP, entities, or priority");
      }

      const requestBody: CreateServiceOrderRequest = {
        remote_utility_id: utilityId,
        sops: formData.selectedEntities.map((entity) => ({
          sop: formData.selectedSOP.id,
          request_username: entity.name,
          request_account_no:
            entity.accountNo || entity.meterNo || entity.assetNo,
          request_id: entity.originalData?.id || parseInt(entity.id),
          area: entity.area,
          sub_area: entity.subArea,
          premise: entity.premise,
          latitude: entity.latitude,
          longitude: entity.longitude,
          status: formData.fieldForceId ? 2 : 9, // 2 = assigned, 9 = pending
          mobile_number: entity.phoneNo,
          priority: formData.priority.toUpperCase(),
          field_force: formData.fieldForceId,
          additional_data: {
            notes: formData.notes || "",
          },
        })),
      };

      return wxApi.create(requestBody);
    },
    {
      onSuccess: (data, variables) => {
        // Determine if orders were created as pending or assigned
        const isAssigned = !!variables.fieldForceId;

        if (isAssigned) {
          // If field force was assigned, invalidate assigned queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey.length >= 4 &&
                queryKey[0] === "wx" &&
                queryKey[1] === "job-card" &&
                queryKey[2] === "assigned" &&
                queryKey[3] === utilityId
              );
            },
          });

          queryClient.invalidateQueries({
            queryKey: ["wx", "job-card", "assigned", "summary"],
          });
        } else {
          // If no field force assigned, invalidate pending queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey.length >= 4 &&
                queryKey[0] === "wx" &&
                queryKey[1] === "job-card" &&
                queryKey[2] === "pending" &&
                queryKey[3] === utilityId
              );
            },
          });

          queryClient.invalidateQueries({
            queryKey: ["wx", "job-card", "pending", "summary"],
          });
        }

        toast({
          title: "Success",
          description: "Service order created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message,
          variant: "destructive",
        });
      },
    }
  );
};

export const useJobCardRecommendation = (
  params?: Partial<JobCardRecommendationParams>,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryParams: JobCardRecommendationParams = {
    remoteUtilityId,
    fieldForceId: params?.fieldForceId || "",
    ...params,
  };

  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.recommendation(),
    () => wxApi.recommendation(queryParams),
    {
      enabled: !!(
        remoteUtilityId &&
        params?.fieldForceId &&
        options.enabled !== false
      ),
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useFieldForceRecommend = (
  params?: Partial<FieldForceRecommendParams>,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryParams: FieldForceRecommendParams = {
    remoteUtilityId,
    sopId: "",
    searchData: "",
    page: 1, // Add default page
    limit: 5, // Add default limit
    ...params,
  };

  const isEnabled = !!(
    remoteUtilityId &&
    params?.sopId &&
    options.enabled !== false
  );

  // Create query key that includes search and pagination parameters
  const queryKey = [
    ...QueryKeyFactory.module.wx.dispatcher.fieldForceRecommend(),
    queryParams.sopId,
    queryParams.meterLocations,
    queryParams.searchData || "",
    queryParams.page, // Add page to query key
    queryParams.limit, // Add limit to query key
  ];

  return useSmartQuery(
    queryKey,
    () => {
      return wxApi.fieldForceRecommend(queryParams);
    },
    {
      enabled: isEnabled,
      staleTime: 0,
    }
  );
};

export const useJobCardAssignment = () => {
  const queryClient = useQueryClient();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    async (assignmentData: AssignmentRequest) => {
      return assignJobCards(assignmentData);
    },
    {
      onSuccess: (data, variables) => {
        console.log(
          "🔄 Job card assignment successful, invalidating caches..."
        );

        // Invalidate PENDING queries (items removed from pending)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "pending" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Invalidate ASSIGNED queries (items added to assigned)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "assigned" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Invalidate summary queries
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "pending", "summary"],
        });
        queryClient.refetchQueries({
          queryKey: ["wx", "job-card", "assigned"],
        });
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "assigned", "summary"],
        });

        // Invalidate detail queries for all assigned job cards
        if (variables.job_card_ids) {
          variables.job_card_ids.forEach((id: number) => {
            queryClient.invalidateQueries({
              queryKey: QueryKeyFactory.module.wx.dispatcher.details(
                id.toString()
              ),
            });
          });
        }
      },
    }
  );
};

export const useJobCardReassignment = () => {
  const queryClient = useQueryClient();
  const remoteUtilityId = useRemoteUtilityId(); // Get the current utility ID

  return useSmartMutation(
    async (assignmentData: AssignmentRequest) => {
      return assignJobCards(assignmentData);
    },
    {
      onSuccess: (data, variables) => {
        // This matches the exact pattern used in useServiceOrdersWithFilters
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "assigned" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Also invalidate summary queries
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "assigned", "summary"],
        });

        // Invalidate detail queries for all reassigned job cards
        if (variables.job_card_ids) {
          variables.job_card_ids.forEach((id: number) => {
            queryClient.invalidateQueries({
              queryKey: QueryKeyFactory.module.wx.dispatcher.details(
                id.toString()
              ),
            });
          });
        }
      },
    }
  );
};

export const usePendingSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.pendingSummary(),
    () => wxApi.pendingSummary({ remoteUtilityId }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useAssignedSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.assignedSummary(),
    () => wxApi.assignedSummary({ remoteUtilityId }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCompletedSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.completedSummary(),
    () => wxApi.completedSummary({ remoteUtilityId }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useHistorySummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.historySummary(),
    () => wxApi.historySummary({ remoteUtilityId }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};
export const useDownloadReport = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    async ({
      reportId,
      filename = "report.pdf", // or whatever default extension you expect
      tabName,
    }: {
      reportId: number;
      filename?: string;
      tabName?: string;
    }) => {
      const blob = await wxApi.downloadReport(
        reportId,
        remoteUtilityId,
        tabName
      );
      downloadFile(blob, filename);
      return blob;
    }
  );
};
export const useRejectJobCard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async ({ jobCardId }: { jobCardId: number }) => {
      return await wxApi.rejectJobCard(jobCardId, remoteUtilityId);
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate COMPLETED queries (item removed or status changed)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "completed" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Might move back to ASSIGNED or PENDING - invalidate those too
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              (queryKey[2] === "assigned" || queryKey[2] === "pending") &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Invalidate summary queries
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "completed", "summary"],
        });
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "assigned", "summary"],
        });
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "pending", "summary"],
        });
      },
    }
  );
};

export const useAcceptJobCard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async ({ jobCardId }: { jobCardId: number }) => {
      return await wxApi.acceptJobCard(jobCardId, remoteUtilityId);
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate COMPLETED queries (item status changed)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "completed" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Might also need to invalidate HISTORY if accepted items move there
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 4 &&
              queryKey[0] === "wx" &&
              queryKey[1] === "job-card" &&
              queryKey[2] === "history" &&
              queryKey[3] === remoteUtilityId
            );
          },
        });

        // Invalidate summary queries
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "completed", "summary"],
        });
        queryClient.invalidateQueries({
          queryKey: ["wx", "job-card", "history", "summary"],
        });

        console.log(
          "✅ Completed and History caches invalidated for acceptance"
        );
      },
    }
  );
};
export const useAreaConfig = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [
      ...QueryKeyFactory.module.wx.dispatcher.territoryConfig(),
      "area", // Add level identifier
      utilityId,
    ],
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return serviceProcedureApi.getTerritoryConfig(utilityId, "area");
    },
    {
      enabled: !!utilityId,
      staleTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: false,
    }
  );
};
export const useServiceAssociations = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.serviceAssociations(),
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      return serviceProcedureApi.getServiceAssociations(utilityId);
    }
  );
};
export const useJobCardTaskReport = (
  jobCardId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryKey = useMemo(
    () => ["wx", "job-card", "report", "task", jobCardId, remoteUtilityId],
    [jobCardId, remoteUtilityId]
  );

  return useSmartQuery(
    queryKey,
    () => {
      if (!jobCardId || !remoteUtilityId) {
        throw new Error("Missing jobCardId or remoteUtilityId");
      }

      return wxApi.getTaskReport(jobCardId, remoteUtilityId);
    },
    {
      enabled: !!(remoteUtilityId && jobCardId && options.enabled !== false),
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: 3,
      retryDelay: 1000,
    }
  );
};

export const useJobCardMaterialReport = (
  jobCardId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryKey = useMemo(
    () => ["wx", "job-card", "report", "material", jobCardId, remoteUtilityId],
    [jobCardId, remoteUtilityId]
  );

  return useSmartQuery(
    queryKey,
    () => {
      if (!jobCardId || !remoteUtilityId) {
        throw new Error("Missing jobCardId or remoteUtilityId");
      }

      return wxApi.getMaterialReport(jobCardId, remoteUtilityId);
    },
    {
      enabled: !!(remoteUtilityId && jobCardId && options.enabled !== false),
      staleTime: 2 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
    }
  );
};

export const useJobCardReadingsReport = (
  jobCardId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryKey = useMemo(
    () => ["wx", "job-card", "report", "readings", jobCardId, remoteUtilityId],
    [jobCardId, remoteUtilityId]
  );

  return useSmartQuery(
    queryKey,
    () => {
      if (!jobCardId || !remoteUtilityId) {
        throw new Error("Missing jobCardId or remoteUtilityId");
      }

      return wxApi.getReadingsReport(jobCardId, remoteUtilityId);
    },
    {
      enabled: !!(remoteUtilityId && jobCardId && options.enabled !== false),
      staleTime: 2 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
    }
  );
};
export const useJobCardSignatureReport = (
  jobCardId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryKey = useMemo(
    () => ["wx", "job-card", "report", "signature", jobCardId, remoteUtilityId],
    [jobCardId, remoteUtilityId]
  );

  return useSmartQuery(
    queryKey,
    () => {
      if (!jobCardId || !remoteUtilityId) {
        throw new Error("Missing jobCardId or remoteUtilityId");
      }

      return wxApi.getSignatureReport(jobCardId, remoteUtilityId);
    },
    {
      enabled: !!(remoteUtilityId && jobCardId && options.enabled !== false),
      staleTime: 2 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
    }
  );
};
export const useRejectionReason = () => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.rejectionReason(),
    () => serviceProcedureApi.getRejectionReason()
  );
};
export const usePriority = () => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.priorityData(),
    () => serviceProcedureApi.getPriority()
  );
};
export const useRejectServiceOrder = () => {
  return useSmartMutation(({ id, payload }: { id: string; payload: any }) =>
    serviceProcedureApi.rejectServiceOrder(id, payload)
  );
};
export const useReworkOrder = () => {
  return useSmartMutation(({ id, payload }: { id: string; payload: any }) =>
    serviceProcedureApi.reworkOrder(id, payload)
  );
};
export const useRejectedSO = (params: {
  remote_utility_id: string;
  is_rejected: string;
  page: any;
  limit: any;
  search_data?: string;
}) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.wx.dispatcher.rejectedSO(params),
      params.search_data || "",
    ],
    () => serviceProcedureApi.getRejectedSO(params)
  );
};

export const useRejectedSODetail = (params: {
  remote_utility_id: string;
  id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.rejectedSODetail(params),
    () => serviceProcedureApi.getRejectedSODetail(params)
  );
};

export const useRejectedSummary = (params: { remote_utility_id: string }) => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.rejectedSummary(params),
    () => serviceProcedureApi.getRejectedSummary(params)
  );
};

export const useAssignedJobCards = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => assignedJobCards(payload), {
    onSuccess: (data, payload) => {
      toast({
        title: "Success",
        description: data,
      });
    },
    onError: (e) => {
      toast({ title: e?.message, variant: "destructive" });
    },
  });
};

export const useUpdateJobCardPriority = () => {
  const queryClient = useQueryClient();
  
  return useSmartMutation(
    (payload: { job_card_id: number; priority: number; remote_utility_id: number }) =>
      updateJobCardPriority(payload),
    {
      //useJobCardDetail
      onSuccess: (data, payload) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.wx.dispatcher.details(payload.job_card_id.toString()),
        });
      },
     
    }
  );
};

export const useGetSoCounts = (startDate?: string, endDate?: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.soCounts(utilityId, startDate, endDate),
    () => getSoCounts(startDate, endDate, utilityId),
    {
      enabled: !!(startDate && endDate && utilityId),
    }
  );
};

export const useGenerateReport = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: {
      remote_utility_id: number | string;
      groups: string[];
      start_date?: string;
      end_date?: string;
    }) => generateReport(payload),
    {
      onSuccess: (data) => {
        toast({
          title: "Export Initiated",
          description: data?.message,
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message,
          variant: "destructive",
        });
      }
    }
  );
};

export const useGetGeneratedReport = (params: {
  remote_utility_id: number | string;
  start_date?: string;
  end_date?: string;
  groups?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.dispatcher.generatedReport(params),
    () => getGeneratedReport(params),
    {
      enabled: !!(params.remote_utility_id && params.start_date && params.end_date && params.groups && params.groups.length > 0),
    }
  );
};